<template>
  <div>
    <div v-if="$i18n.locale === 'pe'">
      <Message message="" icon="coming_soon_cl.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'br'">
      <Message message="" icon="coming_soon_br.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'cl'">
      <div class="card-item mt-6">
        <h3 class="has-text-centered">Solicitudes de Portabilidad Financiera</h3>
        <h4 v-if="!isLoading && pendingRequests.length === 0">
          Felicitaciones, actualmente no hay solicitudes que atender
        </h4>
        <div id="filter-buttons" class="field is-grouped">
          <p v-if="pendingRequests.length" class="control">
            <b-dropdown v-model="selectedOptions" aria-role="list">
              <button id="filter-button-dropdown" slot="trigger" slot-scope="{ active }" class="button is-primary">
                <span>{{ selectedOptions }}</span>
                <b-icon :icon="active ? 'angle-up' : 'angle-down'" />
              </button>
              <b-dropdown-item id="filter-button-all" value="Todas" aria-role="listitem" @click="setTypeFilter('ALL')">
                Todas
              </b-dropdown-item>
              <b-dropdown-item id="filter-button-debtor" value="Clientes" aria-role="listitem" @click="setTypeFilter('CLIENTS')">
                Clientes
              </b-dropdown-item>
              <b-dropdown-item
                id="filter-button-financial-institution"
                value="Instituciones Financieras"
                aria-role="listitem"
                @click="setTypeFilter('FINANCIAL_INSTITUTION')">
                Instituciones Financieras
              </b-dropdown-item>
            </b-dropdown>
          </p>
          <p class="control">
            <button id="download-button-report" class="button is-rounded" @click="downloadReport()">
              <b-icon :icon="'download'" />
              <span>Descargar informe</span>
            </button>
          </p>
        </div>
        <h4 v-if="pendingRequests.length" class="search-heading">
          Solicitudes abiertas
          <b-field horizontal class="field is-pulled-right">
            <b-input
              id="search-input"
              v-model="searchInputVal"
              type="search"
              icon="search"
              icon-clickable
              placeholder="nombre, apellido, rut, email"
              size="is-small"
              rounded
              @input="filterPendingRequests" />
          </b-field>
        </h4>
        <transition-group
          v-if="!isLoading"
          name="requests-transition"
          class="card-requests"
          appear
          tag="div">
          <div
            v-for="(request, i) in filteredRequests"
            :key="`request-${i}`"
            :style="{'--i': i}"
            class="request card-item"
            :class="{active: extendedRequest === i}">
            <div class="request-info">
              <div :id="`id-number-${i}`"> <span>RUT:</span> {{ formatIdNumber(request.debtor.idNumber) }} </div>
              <div>
                <span>Solicita:</span>
                {{
                  request.sender === 'DEBTOR'
                    ? 'Cliente'
                    : request.sender === 'DEBTOR_PORTABILITY'
                      ? 'Cliente - Portabilidad'
                      : 'Institución financiera'
                }}
              </div>
              <div> <span>Fecha de solicitud:</span> {{ formatDate(request.createdAt) }} </div>
              <div> <span>Días en espera:</span> {{ request.lateDays }} </div>
              <div>
                <a
                  :id="`extend-button-${i}`"
                  href="#"
                  @click.prevent="extendedRequest = (extendedRequest === i) ? undefined : i">
                  <span
                    class="fa fa-lg transition-slow"
                    :class="extendedRequest === i ? 'fa-minus-circle' : 'fa-plus-circle'" />
                </a>
              </div>
            </div>
            <div :id="`financial-portability-request-details-${i}`" class="request-content transition-fast">
              <div class="request-details">
                <div class="flex-box-content">
                  <div class="req-row">
                    <div class="req-column">
                      <h4>Datos de la solicitud</h4>
                      <ul>
                        <section v-if="request.sender === 'FINANCIAL_INSTITUTION'">
                          <li><h5>Institución financiera</h5>{{ request.financialInstitution.businessName }} </li>
                          <li><h5>Nombre de contacto</h5>{{ request.financialInstitution.contact.name }}</li>
                        </section>
                        <li>
                          <h5>Correo electrónico</h5>
                          {{ request[request.sender === 'DEBTOR' || 'DEBTOR_PORTABILITY' ? 'debtor'
                            : 'financialInstitution'].contact.email }}
                        </li>
                        <li><h5>Nº de solicitud</h5><small class="is-size-7">{{ request._id }}</small></li>
                        <li v-if="request.companyOperationsIds">
                          <div class="companyOpId">
                            <div>
                              <h5>Productos a portabilizar</h5><small class="is-size-7">
                                <p v-for="x in request.companyOperationsIds" :key="x">
                                  {{ x.idProduct }}
                                </p>
                              </small>
                            </div>
                            <div>
                              <h5 class="typeCredit">Tipo de crédito</h5><small class="is-size-7">
                                <p v-for="x in request.companyOperationsIds" :key="x">
                                  {{ x.typeProduct }}
                                </p>
                              </small>
                            </div>
                          </div>
                        </li>
                        <li v-if="request.operationsAddresses">
                          <h5>Propiedades</h5><small class="is-size-7">
                            <p v-for="x in request.operationsAddresses" :key="x.streetName">
                              {{ `${x.streetName} #${x.streetNumber} ${x.block ? x.block : ''}, ${x.commune}` }}
                            </p>
                          </small>
                        </li>
                        <li>
                          <h5>¿Incluir todos los productos?</h5>
                          <small class="is-size-7">{{ request.includeAllProducts ? 'Sí' : 'No' }}</small>
                        </li>
                        <li>
                          <h5>Deudor</h5><small class="is-size-7">{{ request.debtor.name }} {{ request.debtor.lastName }}</small>
                        </li>
                      </ul>
                    </div>
                    <div class="req-column">
                      <h4>Documentos</h4>
                      <ul>
                        <li
                          v-for="(field, j) in fieldTypes.filter(x=>x.allowedSenders.includes(request.sender))"
                          :id="`li-${field.id}-${i}`"
                          :key="`field-${j}`">
                          <h5>{{ field.text }}</h5>
                          <p v-if="request[`${field.id}Link`]" :id="`${field.id}-${i}`">
                            <i class="fa fa-check" />
                            Ya existe un documento, puedes sobre-escribirlo
                          </p>
                          <div class="upload-buttongroup field has-addons">
                            <div class="control">
                              <div class="file">
                                <label class="file-label">
                                  <input
                                    type="file"
                                    :disabled="checkUserFeature('element.portability-support.button.subir-documentos') === false"
                                    class="file-input"
                                    @change="addFile(request.originalIndex, field.id, $event)">
                                  <span class="file-cta">
                                    <span class="file-icon"><i class="fas fa-upload" /></span>
                                    <span class="file-label">Elige</span>
                                  </span>
                                </label>
                              </div>
                            </div>
                            <div
                              v-if="supportResponse[request.originalIndex] && supportResponse[request.originalIndex][field.id]"
                              class="control">
                              <button
                                :disabled="isUploading[request.originalIndex] && isUploading[request.originalIndex][`${field.id}`]"
                                class="button is-small is-rounded is-info"
                                @click="uploadFile(request.originalIndex, request._id, field.id)">
                                <span
                                  v-if="isUploading[request.originalIndex] && isUploading[request.originalIndex][`${field.id}`]">
                                  <i class="fas fa-spinner fa-spin" /></span>
                                <span v-else> Subir </span>
                              </button>
                            </div>
                          </div>
                          <span
                            v-if="supportResponse[request.originalIndex] && supportResponse[request.originalIndex][field.id]"
                            class="is-size-7">
                            {{ supportResponse[request.originalIndex][field.id].name }}
                          </span>
                        </li>
                        <li v-if="request.sender === 'FINANCIAL_INSTITUTION'">
                          <h5>Solicitud firmada por el cliente</h5>
                          <button
                            :id="`button-download-request-document-${i}`"
                            :disabled="
                              supportResponse[request.originalIndex] &&
                                supportResponse[request.originalIndex].isDownloadingRequestDocument"
                            class="button is-button-secondary"
                            @click="getClientRequestDocument(request._id, request.originalIndex)">
                            <span class="icon is-small"><i class="fas fa-file-download" /></span>
                            <span
                              v-if="
                                supportResponse[request.originalIndex] &&
                                  supportResponse[request.originalIndex].isDownloadingRequestDocument">
                              <i class="fas fa-spinner fa-spin" />
                            </span>
                            <span v-else>Descarga</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="buttons save-changes is-pulled-right">
                  <button
                    :id="`button-reject-${i}`"
                    :disabled="!checkUserFeature('element.portability-support.button.no-atender')"
                    class="button is-button-secondary"
                    @click="confirmRejectRequest(request._id)">
                    No atender
                  </button>
                  <button
                    :id="`button-accept-${i}`"
                    :disabled="!checkUserFeature('element.portability-support.button.aceptar')"
                    class="button is-button-primary"
                    @click="confirmAcceptRequest(request._id)">
                    Aceptar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
      <div class="spinner-content" :class="isLoading ? 'is-loading' : 'is-hidden'">
        <div class="spinner">
          <div class="double-bounce1" />
          <div class="double-bounce2" />
        </div>
      </div>
      <b-modal id="modal-accept" v-model="acceptModal.isActive" :can-cancel="false">
        <div class="modal-card-body">
          <a class="close-modal" @click="acceptModal.isActive=false">
            <img src="@/ui/assets/img/plus-button.svg">
          </a>
          <h3 class="modal-title">Confirmar</h3>
          <section class="modal-card-body">
            <div class="field">
              <label class="label">¿Realmente quieres confirmar la solicitud?
                Se enviarán los archivos adjuntos a la institución</label>
              <p
                v-if="acceptModal.messageError"
                id="modal-accept-error-message"
                class="help is-danger">
                {{ errorDictionary.acceptRequest }}
              </p>
            </div>
          </section>
          <div class="buttons is-pulled-right">
            <b-button
              id="button-cancel-accept"
              class="is-button-secondary"
              rounded
              size="is-small"
              @click="acceptModal.isActive = false">
              Cancelar
            </b-button>
            <b-button
              id="button-confirm-accept"
              :disabled="rejectModal.disableButton"
              class="is-button-primary"
              rounded
              size="is-small"
              @click="acceptRequest(acceptModal.requestId)">
              Aceptar solicitud
            </b-button>
          </div>
        </div>
      </b-modal>
      <b-modal id="modal-reject" v-model="rejectModal.isActive" :can-cancel="false">
        <div class="modal-card-body">
          <a class="close-modal" @click="rejectModal.isActive=false">
            <img src="@/ui/assets/img/plus-button.svg">
          </a>
          <h3 class="modal-title">Motivo por el cual no podemos atender la solicitud</h3>
          <section class="modal-card-body">
            <div class="field">
              <label class="label">Mensaje</label>
              <div class="control">
                <textarea id="reject-comment" v-model="rejectModal.message" class="textarea" />
              </div>
              <p v-if="rejectModal.messageError" class="help is-danger">Debes escribir un mensaje</p>
            </div>
            <p v-if="rejectModal.backendError" id="modal-reject-error-message" class="help is-danger">
              {{ errorDictionary.rejectRequest }}
            </p>
          </section>
          <div class="buttons is-pulled-right">
            <b-button
              id="button-cancel-reject"
              class="is-button-secondary"
              rounded
              size="is-small"
              @click="rejectModal.isActive = false">
              Cancelar
            </b-button>
            <b-button
              id="button-confirm-reject"
              :disabled="rejectModal.disableButton"
              class="is-button-primary"
              rounded
              size="is-small"
              @click="rejectRequest(rejectModal.requestId, rejectModal.message)">
              No atender
            </b-button>
          </div>
        </div>
      </b-modal>
      <b-modal id="modal-download" v-model="downloadModal.isActive" :can-cancel="false">
        <div class="modal-card-body">
          <h3 class="modal-title">Error</h3>
          <section class="modal-card-body">
            <div class="field">
              <label
                id="modal-download-file-error-message"
                class="label help is-danger">
                <i class="fas fa-times-circle" />
                {{ errorDictionary.downloadFile }}
              </label>
            </div>
          </section>
          <div class="buttons is-pulled-right">
            <button
              id="button-confirm-accept"
              class="button is-button-primary"
              @click="downloadModal.isActive = false">
              Ok
            </button>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import
{
  acceptFinancialPortabilityRequest,
  rejectFinancialPortabilityRequest,
  getPendingFinancialPortabilityRequests,
  addValuationToFinancialPortabilityRequest,
  addTitleStudyToFinancialPortabilityRequest,
  addDebtSettlementToFinancialPortabilityRequest,
  addTaxesCertificateToFinancialPortabilityRequest,
  getRequestDocumentFromFinancialPortabilityRequest,
  getReportFinancialPortabilityRequests
} from '@/application/services/api-clients/financial-portability';
import { dateToFormatedString, now, getDaysDifferenceBetweenDays } from '@/infrastructure/date';
import { generateExcel } from '@/infrastructure/excel';
import userFeatures from '@/ui/mixins/user-features';
import Message from '@/ui/components/Message.vue';
import { formatNumber } from '@/infrastructure/format';

export default {
  name: 'PortabilitySupport',
  components: { Message },
  mixins: [userFeatures],
  data() {
    return {
      selectedOptions: 'Filtrar',
      rejectComment: '',
      rejectId: '',
      typeFilter: 'ALL',
      searchInputVal: '',
      pendingRequests: [],
      filteredRequests: [],
      isLoading: true,
      isUploading: [],
      isDownloading: [],
      supportResponse: [],
      extendedRequest: undefined,
      fieldTypes: [
        {
          id: 'titleStudy',
          text: 'Estudio de Títulos',
          uploadFunction: addTitleStudyToFinancialPortabilityRequest,
          allowedSenders: ['FINANCIAL_INSTITUTION', 'DEBTOR_PORTABILITY']
        },
        {
          id: 'valuation',
          text: 'Tasación',
          uploadFunction: addValuationToFinancialPortabilityRequest,
          allowedSenders: ['FINANCIAL_INSTITUTION', 'DEBTOR_PORTABILITY']
        },
        {
          id: 'debtSettlement',
          text: 'Liquidación de Deuda',
          uploadFunction: addDebtSettlementToFinancialPortabilityRequest,
          allowedSenders: ['FINANCIAL_INSTITUTION', 'DEBTOR', 'DEBTOR_PORTABILITY']
        },
        {
          id: 'taxesCertificate',
          text: 'Certificado de timbres y estampillas',
          uploadFunction: addTaxesCertificateToFinancialPortabilityRequest,
          allowedSenders: ['FINANCIAL_INSTITUTION', 'DEBTOR_PORTABILITY']
        }
      ],
      errorDictionary: {
        acceptRequest: 'Es necesario adjuntar todos los archivos para poder aceptar la solicitud',
        rejectRequest: 'Hubo un error al no atender la solicitud',
        uploadFile: 'Hubo un problema al subir el archivo',
        downloadFile: 'Ocurrió un error al tratar de descargar el archivo'
      },
      rejectModal: {
        message: '',
        requestId: '',
        isActive: false,
        messageError: false,
        backendError: false,
        disableButton: false
      },
      acceptModal: {
        requestId: '',
        isActive: false,
        messageError: false,
        disableButton: false
      },
      downloadModal: {
        isActive: false
      }
    };
  },
  async mounted() {
    if (this.$i18n.locale === 'cl') {
      await this.getPendingRequests();
      await this.filterPendingRequests();
    }
  },
  methods: {
    async updatePendingRequests() {
      this.supportResponse = [];
      this.isUploading = [];
      await this.$nextTick();
      this.searchInputVal = '';
      await this.getPendingRequests();
      await this.filterPendingRequests();
    },
    async getPendingRequests() {
      this.isLoading = true;
      this.pendingRequests = await getPendingFinancialPortabilityRequests();
      const responseTemplate = {};
      this.fieldTypes.forEach(field => { responseTemplate[field.id] = false; });
      this.supportResponse = this.pendingRequests.map(() => JSON.parse(JSON.stringify(responseTemplate)));
      this.isUploading = this.pendingRequests.map(() => JSON.parse(JSON.stringify(responseTemplate)));
      this.isLoading = false;
    },
    formatDate: (date, format = 'DD/MM/YYYY') => dateToFormatedString(date, format),
    today: (format = 'DD/MM/YYYY') => now(format),
    formatIdNumber(idNumber) {
      const idNumberBody = idNumber.split('-');
      return `${formatNumber({ number: Number(idNumberBody[0]) })}-${idNumberBody[1]}`;
    },
    addFile(i, fieldType, event) {
      const [file] = event.target.files;
      this.supportResponse[i][fieldType] = file;
    },
    async uploadFile(i, id, documentType) {
      this.isUploading[i][documentType] = true;
      const file = this.supportResponse[i][documentType];
      try {
        const { uploadFunction } = this.fieldTypes.find(f => f.id === documentType);
        await uploadFunction(id, file);
        this.pendingRequests[i][`${documentType}Link`] = true;
        this.supportResponse[i][documentType] = null;
      } catch (_error) {
        this.errorAlert(this.errorDictionary.uploadFile);
      }
      this.isUploading[i][documentType] = false;
    },
    async getClientRequestDocument(id, indexOfFinancialPortabilityRequest) {
      this.supportResponse[indexOfFinancialPortabilityRequest].isDownloadingRequestDocument = true;
      try {
        const result = await getRequestDocumentFromFinancialPortabilityRequest(id);
        const file = new Blob(
          [result],
          { type: 'application/pdf' }
        );
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      } catch (_error) {
        this.downloadModal.isActive = true;
      }
      this.supportResponse[indexOfFinancialPortabilityRequest].isDownloadingRequestDocument = false;
    },
    confirmRejectRequest(id) {
      this.rejectModal.message = '';
      this.rejectModal.requestId = id;
      this.rejectModal.isActive = true;
      this.rejectModal.messageError = false;
    },
    confirmAcceptRequest(id) {
      this.acceptModal.requestId = id;
      this.acceptModal.messageError = false;
      this.disableButton = false;
      this.acceptModal.isActive = true;
    },
    async downloadReport() {
      const data = await getReportFinancialPortabilityRequests();
      generateExcel(data, { filename: 'portabilidad-financiera' });
    },
    // errorAlert(text) {
    //   this.$buefy.dialog.alert({
    //     title: 'Error',
    //     message: text,
    //     type: 'is-danger',
    //     hasIcon: true,
    //     icon: 'times-circle',
    //     iconPack: 'fa',
    //     ariaRole: 'alertdialog',
    //     ariaModal: true
    //   });
    // },
    async acceptRequest(id) {
      try {
        this.disableButton = true;
        await acceptFinancialPortabilityRequest(id);
        await this.updatePendingRequests();
        this.acceptModal.isActive = false;
        this.extendedRequest = undefined;
      } catch (_error) {
        this.acceptModal.messageError = true;
        this.disableButton = false;
      }
    },
    async rejectRequest(id, comment) {
      this.rejectModal.backendError = false;
      this.rejectModal.messageError = false;
      if (!comment) {
        this.rejectModal.messageError = true;
        return;
      }
      this.rejectModal.disableButton = true;
      try {
        await rejectFinancialPortabilityRequest(id, comment);
        await this.updatePendingRequests();
        this.rejectModal.isActive = false;
      } catch (_error) {
        this.rejectModal.backendError = true;
      }
      this.rejectModal.disableButton = false;
    },
    parseSearchString: string => `${string}`.toUpperCase().replace(/\./g, ''),
    setTypeFilter(type) {
      this.typeFilter = type;
      this.filterPendingRequests();
    },
    async filterPendingRequests() {
      this.filteredRequests = [];
      await this.$nextTick();
      const requests = this.pendingRequests.map((request, i) => {
        const requestIn = request;
        requestIn.originalIndex = i;
        requestIn.lateDays = getDaysDifferenceBetweenDays(new Date(request.createdAt), new Date());
        return requestIn;
      });
      if (this.typeFilter === 'ALL') {
        this.filteredRequests = requests;
      }
      if (this.typeFilter === 'CLIENTS') {
        this.filteredRequests = requests.filter(request => request.sender !== 'FINANCIAL_INSTITUTION');
      }
      if (this.typeFilter === 'FINANCIAL_INSTITUTION') {
        this.filteredRequests = requests.filter(request => request.sender === 'FINANCIAL_INSTITUTION');
      }
      if (this.searchInputVal === '') return;
      const parsedVal = this.parseSearchString(this.searchInputVal);
      this.filteredRequests = this.filteredRequests.filter(r => {
        const companyOperationsIds = r.companyOperationsIds || [];

        if (!r.financialInstitution) {
          return this.parseSearchString(companyOperationsIds.join(',')).includes(parsedVal)
            || this.parseSearchString(r.debtor.name).includes(parsedVal)
            || this.parseSearchString(r.debtor.lastName).includes(parsedVal)
            || this.parseSearchString(r.debtor.idNumber).includes(parsedVal)
            || this.parseSearchString(r.debtor.contact.email).includes(parsedVal);
        }
        return this.parseSearchString(companyOperationsIds.join(',')).includes(parsedVal)
          || this.parseSearchString(r.debtor.name).includes(parsedVal)
          || this.parseSearchString(r.debtor.lastName).includes(parsedVal)
          || this.parseSearchString(r.debtor.idNumber).includes(parsedVal)
          || this.parseSearchString(r.debtor.contact.email).includes(parsedVal)
          || this.parseSearchString(r.financialInstitution.bussinessName).includes(parsedVal)
          || this.parseSearchString(r.financialInstitution.idNumber).includes(parsedVal)
          || this.parseSearchString(r.financialInstitution.contact.name).includes(parsedVal)
          || this.parseSearchString(r.financialInstitution.contact.email).includes(parsedVal);
      });
    }
  }
};
</script>

<style>
.control {
  font-family: var(--secondary-font);
}
.control a {
  text-decoration: none;
}
  .close-modal {
    position: absolute;
    right: 20px;
    top:20px;
    width: 40px;
  }
  #filter-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    align-content: space-between;
    flex-flow: wrap;
  }
  #filter-buttons p {
    margin-bottom: 10px;
  }
  .search-heading {
    padding: 10px;
    white-space: nowrap;
    overflow: hidden;
  }
  .upload-buttongroup.field {
    margin-bottom: 0;
  }
  .upload-buttongroup button, .upload-buttongroup .file-cta {
    height: 30px;
  }
  .request {
    margin: 15px 0;
    font-size: 0.9rem;
  }
  div.request-info {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
  }
  div.request-info div {
    margin: 10px;
  }
  .request-info div span, .request h5 {
    color: var(--primary);
  }
  div.request-content {
    background: #f9f9f9;
    border-radius: 20px;
    overflow: hidden;
    max-height: 0;
  }
  .flex-box-content {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-around;
  }
  .request.active div.request-content {
    margin-top: 5px;
    padding: 5px 10px;
    min-height: 300px;
    overflow-y: auto;
  }
  .request-details {
    width: 100%;
    padding: 0 5px;
    font-size: 0.9rem;
  }
  .request-details h4 {
    padding-top: 5px;
    margin-bottom: 0;
  }
  .request-details h5 {
    margin-bottom: 0;
  }
  .req-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
  .req-column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
  }
  .save-changes {
    margin: 15px;
    align-self: flex-end;
  }
  /* Transition tickets */
  /* https://codepen.io/shshaw/pen/YLmdxz */
  .requests-transition-enter-active {
    transition: opacity 0.5s linear, transform 0.5s cubic-bezier(0.2, 0.5, 0.1, 1);
    transition-delay: calc( 0.1s * var(--i) );
  }
  .requests-transition-enter, .requests-transition-leave-to, .requests-transition-leave-active {
    opacity: 0;
  }
  .requests-transition-enter {
    transform: translateX(-1em);
  }
  .requests-transition-leave-to {
    transform: translateX(1em);
  }
  .companyOpId {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    .typeCredit {
      margin-bottom: 1.2rem !important;
    }
  }
</style>
